document.documentElement.classList.add('hk-js');

class HKBrandUI {
  constructor(root = document) {
    this.root = root;
    this.observeReveals();
    this.bindScrollers();
    this.bindVariantSelectors();
    this.bindProcessJourneys();
  }

  observeReveals() {
    const elements = this.root.querySelectorAll('.hk-reveal:not([data-hk-ready])');
    if (!elements.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => {
      element.dataset.hkReady = 'true';
      observer.observe(element);
    });
  }

  bindScrollers() {
    this.root.querySelectorAll('[data-hk-scroller]:not([data-hk-ready])').forEach((scroller) => {
      scroller.dataset.hkReady = 'true';
      const section = scroller.closest('.hk-section');
      section?.querySelectorAll('[data-hk-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.hkScroll === 'next' ? 1 : -1;
          scroller.scrollBy({ left: direction * scroller.clientWidth * 0.82, behavior: 'smooth' });
        });
      });
    });
  }

  bindVariantSelectors() {
    this.root.querySelectorAll('[data-hk-variant-selector]:not([data-hk-ready])').forEach((selector) => {
      selector.dataset.hkReady = 'true';

      const tabs = Array.from(selector.querySelectorAll('[data-hk-variant-tab]'));
      const panels = Array.from(selector.querySelectorAll('[data-hk-variant-panel]'));

      const activateTab = (activeTab, moveFocus = false) => {
        const activeId = activeTab.dataset.hkVariantTab;

        tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.hkVariantPanel === activeId;
          panel.hidden = !isActive;
          panel.classList.remove('is-active');
          if (isActive) requestAnimationFrame(() => panel.classList.add('is-active'));
        });

        if (moveFocus) activeTab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(tab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex;

          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === undefined) return;

          event.preventDefault();
          activateTab(tabs[nextIndex], true);
        });
      });

      selector.addEventListener('shopify:cart:lines-update', (event) => {
        const sourceCard = event.target instanceof Element ? event.target.closest('.hk-variant-card') : null;
        if (!sourceCard || !selector.contains(sourceCard) || event.action !== 'add') return;

        const openCartDrawer = () => {
          const cartDrawerTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
          if (!cartDrawerTrigger || cartDrawerTrigger.getAttribute('aria-expanded') === 'true') return;
          cartDrawerTrigger.click();
        };

        if (event.promise) {
          event.promise
            .then(({ detail }) => {
              if (!detail?.didError) window.setTimeout(openCartDrawer, 120);
            })
            .catch(() => {});
        } else {
          window.setTimeout(openCartDrawer, 120);
        }
      });
    });
  }

  bindProcessJourneys() {
    this.root.querySelectorAll('[data-hk-process-journey]:not([data-hk-process-ready])').forEach((journey) => {
      journey.dataset.hkProcessReady = 'true';

      const tabs = Array.from(journey.querySelectorAll('[data-hk-process-step]'));
      const panels = Array.from(journey.querySelectorAll('[data-hk-process-panel]'));
      const tablist = journey.querySelector('[role="tablist"]');
      const mobileMedia = window.matchMedia('(max-width: 749px)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const autoplay = journey.dataset.hkProcessAutoplay === 'true' && !reducedMotion;
      const interval = Math.max(Number(journey.dataset.hkProcessInterval) || 5000, 3000);
      let activeIndex = Math.max(tabs.findIndex((tab) => tab.classList.contains('is-active')), 0);
      let timer;

      const updateOrientation = () => tablist?.setAttribute('aria-orientation', mobileMedia.matches ? 'horizontal' : 'vertical');

      const activateStep = (index, options = {}) => {
        activeIndex = (index + tabs.length) % tabs.length;
        const activeTab = tabs[activeIndex];
        const activeId = activeTab.dataset.hkProcessStep;

        tabs.forEach((tab, tabIndex) => {
          const isActive = tabIndex === activeIndex;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.hkProcessPanel === activeId;
          panel.hidden = !isActive;
          panel.classList.remove('is-active');
          if (isActive) requestAnimationFrame(() => panel.classList.add('is-active'));
        });

        if (options.focus) activeTab.focus();
        if (options.scroll && mobileMedia.matches) {
          const slider = activeTab.parentElement;
          const centeredPosition = activeTab.offsetLeft - (slider.clientWidth - activeTab.offsetWidth) / 2;
          slider.scrollTo({ left: Math.max(0, centeredPosition), behavior: reducedMotion ? 'auto' : 'smooth' });
        }
      };

      const stopAutoplay = () => {
        window.clearInterval(timer);
        timer = undefined;
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (!autoplay || document.hidden || tabs.length < 2) return;
        timer = window.setInterval(() => activateStep(activeIndex + 1, { scroll: true }), interval);
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          activateStep(index, { scroll: true });
          startAutoplay();
        });

        tab.addEventListener('keydown', (event) => {
          let nextIndex;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = activeIndex + 1;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = activeIndex - 1;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === undefined) return;

          event.preventDefault();
          activateStep(nextIndex, { focus: true, scroll: true });
          startAutoplay();
        });
      });

      journey.addEventListener('mouseenter', stopAutoplay);
      journey.addEventListener('mouseleave', startAutoplay);
      journey.addEventListener('focusin', stopAutoplay);
      journey.addEventListener('focusout', (event) => {
        if (!journey.contains(event.relatedTarget)) startAutoplay();
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? stopAutoplay() : startAutoplay()));
      mobileMedia.addEventListener?.('change', updateOrientation);

      updateOrientation();
      activateStep(activeIndex);
      startAutoplay();
    });
  }
}

const initializeHKBrand = (root = document) => new HKBrandUI(root);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHKBrand());
} else {
  initializeHKBrand();
}

document.addEventListener('shopify:section:load', (event) => initializeHKBrand(event.target));
document.addEventListener('shopify:block:select', (event) => {
  if (!event.detail?.blockId) return;

  const section = event.target.closest('[data-hk-variant-selector]');
  if (section) {
    const matchingTab = Array.from(section.querySelectorAll('[data-hk-block-id]')).find(
      (tab) => tab.dataset.hkBlockId === event.detail.blockId
    );
    matchingTab?.click();
  }

  const journey = event.target.closest('[data-hk-process-journey]');
  if (journey) {
    const matchingStep = Array.from(journey.querySelectorAll('[data-hk-process-block-id]')).find(
      (step) => step.dataset.hkProcessBlockId === event.detail.blockId
    );
    matchingStep?.click();
  }
});
